import React, { useEffect } from 'react';
import { Upload, Image, Typography, message, Space, Button } from 'antd';
import { InboxOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import useIsMobile from '@/hooks/useIsMobile';

const { Dragger } = Upload;
const { Text } = Typography;

type ImageUploaderVariant = 'thumbnail' | 'hero';

type Props = {
  existingUrl?: string | null;
  previewUrl?: string;
  onChange: (next: { file: File | null; previewUrl: string | undefined }) => void;
  onRemove: () => void;
  variant?: ImageUploaderVariant;
};

export function ImageUploader({
  existingUrl,
  previewUrl,
  onChange,
  onRemove,
  variant = 'thumbnail',
}: Props) {
  const displayUrl = previewUrl || existingUrl;
  const isMobile = useIsMobile(768);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const uploadProps: UploadProps = {
    accept: 'image/*',
    multiple: false,
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (f) => {
      if (!f.type.startsWith('image/')) {
        message.error('Please upload an image file');
        return Upload.LIST_IGNORE;
      }
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      onChange({ file: f, previewUrl: URL.createObjectURL(f) });
      return false;
    },
  };

  const actionButtons = (
    <Space>
      <Upload {...uploadProps}>
        <Button icon={<UploadOutlined />}>Change Image</Button>
      </Upload>
      <Button danger icon={<DeleteOutlined />} onClick={onRemove}>
        Remove
      </Button>
    </Space>
  );

  if (displayUrl) {
    if (variant === 'hero') {
      return (
        <Space orientation="vertical" style={{ width: '100%' }} size={10}>
          <div
            style={{
              height: isMobile ? 250 : 350,
              overflow: 'hidden',
              backgroundImage: `url(${displayUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '20px 20px 0 0',
              width: '100%',
            }}
          />
          {actionButtons}
        </Space>
      );
    }

    return (
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Image
          src={displayUrl}
          alt="cover"
          style={{ width: '100%', borderRadius: 8, aspectRatio: '16/9', objectFit: 'cover' }}
        />
        {actionButtons}
      </Space>
    );
  }

  return (
    <Dragger {...uploadProps} style={{ padding: 12 }}>
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">Click or drag image to stage</p>
      <Text type="secondary">Preview only. Will upload on Save.</Text>
    </Dragger>
  );
}
